import type { ReactNode } from 'react';
import { Badge, type DataTableColumn, type Variant } from '@jordan/ui';
import { FavouriteHeart } from './watchlist';
import { formatClose, formatDistance, formatMoney, formatTimeLeft } from './maxsoldSearchUtils';

// One normalized model for every Maxsold "listing" (a lot or a whole auction), so the grid card and
// the list columns render identically across Search, Favourites, Bids and Auction detail. Fields are
// optional; each surface fills the slots that apply to it (see the mappers on each page).

export interface ListingBadge {
  label: string;
  variant: Variant;
  soft?: boolean;
}
export interface ListingFav {
  active: boolean;
  pending: boolean;
  onToggle: () => void;
}
export interface ListingView {
  id: number;
  imageUrl: string | null;
  title: string;
  /** Secondary line under the title (e.g. "Auction · Lot 12" or "Toronto, ON"). */
  subtitle?: string | null;
  /** Card overlay / status column badge (bid status or auction category). */
  badge?: ListingBadge | null;
  /** Primary money value (e.g. current bid). `undefined` = not a priced listing (an auction). */
  price?: number | null;
  /** The viewer's proxy/max bid ("Your max"). */
  secondaryPrice?: number | null;
  /** Bid count (gavel icon). */
  bidCount?: number | null;
  /** Lot count (auctions). */
  lotCount?: number | null;
  /** Distance from the search centre, in metres (auctions). */
  distanceMeters?: number | null;
  /** ISO close/end time — drives the Closes + Time left displays. */
  closeTime: string;
  /** Favourite heart control; omit to hide the heart. */
  favourite?: ListingFav | null;
  /** External "View on MaxSold" URL (list actions column). */
  externalUrl?: string | null;
  /** External directions / map URL (list actions column). */
  mapUrl?: string | null;
}

const CLAMP2: React.CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

/** "Time left" text with shared urgency styling (bold red under 6h, muted when ended). */
export function TimeLeft({ iso, now }: { iso: string; now: number }) {
  const t = formatTimeLeft(iso, now);
  if (t.ended) return <span className="text-muted">{t.text}</span>;
  return <span className={t.urgent ? 'fw-bold text-danger' : undefined}>{t.text}</span>;
}

/** A square photo with the standard placeholder fallback. */
function Thumb({ url, size }: { url: string | null; size: number }) {
  return url ? (
    <img src={url} alt="" loading="lazy" style={{ width: size, height: size, objectFit: 'cover', borderRadius: 4 }} />
  ) : (
    <div className="bg-light rounded d-flex align-items-center justify-content-center" style={{ width: size, height: size }}>
      <i className="ph-image text-muted" />
    </div>
  );
}

/** The muted stats line on a card: bids · max · distance (whichever apply). */
function CardStats({ v }: { v: ListingView }) {
  const bits: ReactNode[] = [];
  if (v.bidCount != null) {
    bits.push(
      <span key="b">
        <i className="ph-gavel me-1" />
        {v.bidCount}
        {v.price === undefined ? ' bids' : ''}
      </span>,
    );
  }
  if (v.secondaryPrice) bits.push(<span key="m">max {formatMoney(v.secondaryPrice)}</span>);
  if (v.distanceMeters != null) bits.push(<span key="d">{formatDistance(v.distanceMeters)}</span>);
  if (bits.length === 0) return null;
  return (
    <>
      {bits.map((b, i) => (
        <span key={i}>
          {i > 0 ? ' · ' : ''}
          {b}
        </span>
      ))}
    </>
  );
}

/** The standard grid card for a listing (the Favourites design, generalized). Fully clickable. */
export function ListingCard({
  view: v,
  now,
  onOpen,
  surchargePercent = 0,
}: {
  view: ListingView;
  now: number;
  onOpen?: () => void;
  /** Buyer's premium (%): when set, priced cards show the with-surcharge total in brackets. */
  surchargePercent?: number;
}) {
  const isPriced = v.price !== undefined;
  const primary = isPriced ? formatMoney(v.price ?? null) : v.lotCount != null ? `${v.lotCount} lots` : null;
  // e.g. current bid $17 with 18% → "$17 ($20)". Rounded; the bracketed total isn't bold.
  const withSurcharge =
    isPriced && v.price != null && surchargePercent > 0 ? Math.round(v.price * (1 + surchargePercent / 100)) : null;
  return (
    <div
      className="card h-100 shadow-sm"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onOpen?.();
      }}
      style={{ cursor: 'pointer' }}
    >
      <div className="position-relative">
        {v.imageUrl ? (
          <img src={v.imageUrl} alt="" loading="lazy" className="card-img-top" style={{ height: 150, objectFit: 'cover' }} />
        ) : (
          <div className="card-img-top bg-light d-flex align-items-center justify-content-center" style={{ height: 150 }}>
            <i className="ph-image ph-2x text-muted" />
          </div>
        )}
        {v.badge && (
          <div className="position-absolute top-0 start-0 m-2">
            <Badge variant={v.badge.variant} soft={v.badge.soft}>
              {v.badge.label}
            </Badge>
          </div>
        )}
        {v.favourite && (
          <div className="position-absolute top-0 end-0 m-2">
            <FavouriteHeart favourite={v.favourite.active} pending={v.favourite.pending} onToggle={v.favourite.onToggle} />
          </div>
        )}
      </div>
      <div className="card-body p-2 d-flex flex-column">
        <div className="fw-semibold fs-sm mb-1" style={CLAMP2}>
          {v.title}
        </div>
        {v.subtitle && <div className="text-muted fs-sm mb-2 text-truncate">{v.subtitle}</div>}
        <div className="d-flex justify-content-between align-items-end mt-auto gap-2">
          <div className="text-truncate">
            {primary != null && (
              <div className="fw-semibold">
                {primary}
                {withSurcharge != null && (
                  <span className="fw-normal text-muted"> ({formatMoney(withSurcharge)})</span>
                )}
              </div>
            )}
            <div className="text-muted fs-sm text-truncate">
              <CardStats v={v} />
            </div>
          </div>
          <div className="text-end fs-sm flex-shrink-0">
            <div className="text-muted">{formatClose(v.closeTime)}</div>
            <TimeLeft iso={v.closeTime} now={now} />
          </div>
        </div>
      </div>
    </div>
  );
}

export interface ListingColumnOpts {
  now: number;
  /** Header for the title column ("Lot" | "Auction"). */
  titleHeader?: string;
  price?: boolean;
  secondaryPrice?: boolean;
  bids?: boolean;
  lots?: boolean;
  distance?: boolean;
  status?: boolean;
  favourite?: boolean;
  /** Trailing external-link actions (View on MaxSold / Directions). */
  actions?: boolean;
}

/** The standard list columns for a listing, matching the Favourites table. Columns are opt-in. */
export function listingColumns(o: ListingColumnOpts): DataTableColumn<ListingView>[] {
  const cols: DataTableColumn<ListingView>[] = [
    { key: 'image', header: '', className: 'p-1', render: (v) => <Thumb url={v.imageUrl} size={56} /> },
    {
      key: 'title',
      header: o.titleHeader ?? 'Lot',
      sortable: true,
      render: (v) => (
        <div>
          <div className="fw-semibold">{v.title}</div>
          {v.subtitle && <div className="text-muted fs-sm">{v.subtitle}</div>}
        </div>
      ),
    },
  ];
  if (o.price)
    cols.push({
      key: 'price',
      header: 'Current bid',
      align: 'end',
      sortable: true,
      sortValue: (v) => v.price ?? 0,
      render: (v) => formatMoney(v.price ?? null),
    });
  if (o.secondaryPrice)
    cols.push({
      key: 'secondaryPrice',
      header: 'Your max',
      align: 'end',
      sortable: true,
      sortValue: (v) => v.secondaryPrice ?? 0,
      render: (v) => (v.secondaryPrice ? formatMoney(v.secondaryPrice) : <span className="text-muted">—</span>),
    });
  if (o.bids)
    cols.push({
      key: 'bidCount',
      header: 'Bids',
      align: 'center',
      sortable: true,
      sortValue: (v) => v.bidCount ?? 0,
      render: (v) => v.bidCount ?? 0,
    });
  if (o.lots)
    cols.push({
      key: 'lotCount',
      header: 'Lots',
      align: 'center',
      sortable: true,
      sortValue: (v) => v.lotCount ?? 0,
      render: (v) => v.lotCount ?? 0,
    });
  if (o.distance)
    cols.push({
      key: 'distanceMeters',
      header: 'Distance',
      align: 'end',
      sortable: true,
      sortValue: (v) => v.distanceMeters ?? Number.POSITIVE_INFINITY,
      render: (v) => formatDistance(v.distanceMeters ?? null),
    });
  if (o.status)
    cols.push({
      key: 'status',
      header: 'Status',
      align: 'center',
      sortValue: (v) => v.badge?.label ?? '',
      render: (v) =>
        v.badge ? (
          <Badge variant={v.badge.variant} soft={v.badge.soft}>
            {v.badge.label}
          </Badge>
        ) : (
          <span className="text-muted">—</span>
        ),
    });
  cols.push({
    key: 'closeTime',
    header: 'Closes',
    sortable: true,
    sortValue: (v) => Date.parse(v.closeTime) || 0,
    render: (v) => formatClose(v.closeTime),
  });
  cols.push({
    key: 'timeLeft',
    header: 'Time left',
    align: 'end',
    sortable: true,
    sortValue: (v) => Date.parse(v.closeTime) || 0,
    render: (v) => <TimeLeft iso={v.closeTime} now={o.now} />,
  });
  if (o.favourite)
    cols.push({
      key: 'fav',
      header: '',
      align: 'end',
      className: 'p-1',
      render: (v) =>
        v.favourite ? (
          <FavouriteHeart favourite={v.favourite.active} pending={v.favourite.pending} onToggle={v.favourite.onToggle} size={18} />
        ) : null,
    });
  if (o.actions)
    cols.push({
      key: 'actions',
      header: '',
      align: 'end',
      render: (v) => (
        <div className="d-inline-flex gap-1">
          {v.externalUrl && (
            <a
              href={v.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-flat-primary btn-sm"
              title="View on MaxSold"
              onClick={(e) => e.stopPropagation()}
            >
              <i className="ph-arrow-square-out" />
            </a>
          )}
          {v.mapUrl && (
            <a
              href={v.mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-flat-secondary btn-sm"
              title="Directions"
              onClick={(e) => e.stopPropagation()}
            >
              <i className="ph-map-pin" />
            </a>
          )}
        </div>
      ),
    });
  return cols;
}

/** A responsive grid of listing cards. */
export function ListingGrid({
  views,
  now,
  onOpen,
  emptyMessage = 'Nothing to show.',
  surchargePercent = 0,
}: {
  views: ListingView[];
  now: number;
  onOpen: (id: number) => void;
  emptyMessage?: string;
  /** Buyer's premium (%) forwarded to each card for the with-surcharge total. */
  surchargePercent?: number;
}) {
  if (views.length === 0) return <p className="text-muted text-center py-4 mb-0">{emptyMessage}</p>;
  return (
    <div className="row row-cols-2 row-cols-md-3 row-cols-xl-4 g-3">
      {views.map((v) => (
        <div className="col" key={v.id}>
          <ListingCard view={v} now={now} onOpen={() => onOpen(v.id)} surchargePercent={surchargePercent} />
        </div>
      ))}
    </div>
  );
}
